import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterLink } from '@angular/router';
import { KeycloakService } from 'keycloak-angular';
import { FooterComponent } from '../../shared/components/footer/footer.component';
import { SubHeaderComponent } from '../../shared/components/sub-header/sub-header.component';
import { LoginModalComponent } from '../../shared/components/login-modal/login-modal.component';
import { RolesApiService } from '@angular-workspace/services';

@Component({
  selector: 'app-sandbox',
  standalone: true,
  imports: [
    CommonModule,
    RouterLink,
    FooterComponent,
    SubHeaderComponent,
    LoginModalComponent,
  ],
  templateUrl: './sandbox.component.html',
  styleUrl: './sandbox.component.scss',
})
export class SandboxComponent implements OnInit {
  private router = inject(Router);
  private rolesService = inject(RolesApiService);
  private keycloak = inject(KeycloakService);

  showLoginModal = false;

  readonly sandboxSubHeaderDescription =
    'A secure environment to explore, test, and integrate APIs, data services, and platform capabilities. A controlled testing space for partners and developers to build and validate healthcare data solutions on IUDX Health.';

  features = [
    {
      title: 'Data Exploration',
      description: 'Explore structured and unstructured data.',
      image: '/sandbox/Analyze-cuate.svg',
    },
    {
      title: 'AI Model Training',
      description: 'Build, test, and refine AI models using real datasets.',
      image: '/sandbox/Business Plan-pana.svg',
    },
    {
      title: 'Contextual AI',
      description: 'Refine AI solutions using healthcare-specific datasets and exchange assets.',
      image: '/sandbox/Chat bot-amico.svg',
    },
    {
      title: 'Built-In Compute',
      description: 'Access compute directly within your workspace.',
      image: '/sandbox/Digital transformation-pana.svg',
    },
  ];

  capabilities = [
    'MLOps and pipeline tools like MLflow, DVC, Weights & Biases, ZenML, Kubeflow, and ClearML.',
    'Data processing and feature engineering libraries such as Dask, Vaex, Featuretools, and Feast.',
    'Visualization and dashboarding tools including Plotly, Bokeh, HoloViews, and Streamlit.',
    'Model serving and deployment frameworks like BentoML, Seldon Core, TorchServe, and Gradio.',
    'Articles & Guides: Learn from practical notes, tutorials, and framework walkthroughs.',
    'Video Tutorials: Leverage platform tutorials to effectively understand and utilize IUDX Health resources.',
  ];

  async ngOnInit(): Promise<void> {
    try {
      await this.rolesService.getRoles();
    } catch (error) {
      console.error('Error loading roles:', error);
    }
  }

  async onCreateNotebookClick(): Promise<void> {
    try {
      let isLoggedIn = false;
      try {
        await new Promise((resolve) => setTimeout(resolve, 100));
        isLoggedIn = await this.keycloak.isLoggedIn();
      } catch {
        try {
          const token = await this.keycloak.getToken();
          isLoggedIn = !!token;
        } catch {
          const localToken = localStorage.getItem('ai-sandbox-cat-auth-token');
          isLoggedIn = !!localToken;
        }
      }

      if (!isLoggedIn) {
        this.showLoginModal = true;
        return;
      }

      const userRoles = this.rolesService.getUserRoles();

      if (!userRoles || userRoles.length === 0) {
        this.router.navigate(['/consumer/my-projects']);
        return;
      }

      const highestRole = this.rolesService.getHighestRole(userRoles);

      if (!highestRole) {
        this.router.navigate(['/consumer/my-projects']);
        return;
      }

      const normalizedRole = highestRole.toLowerCase().replace(/_/g, '-');

      let route: string;
      switch (normalizedRole) {
        case 'admin':
        case 'cos-admin':
        case 'platform-admin':
          route = '/admin/home';
          break;
        case 'org-admin':
          route = '/org-admin/my-projects';
          break;
        case 'provider':
          route = '/provider/my-projects';
          break;
        case 'consumer':
          route = '/consumer/my-projects';
          break;
        default:
          route = '/consumer/my-projects';
      }

      this.router.navigate([route]);
    } catch (error) {
      console.error('Error navigating to My Projects:', error);
      this.router.navigate(['/consumer/my-projects']);
    }
  }
}
